/* raw layout hash: _default-web-layout:ada46ad0bdc0e54ee50a72667acb7b1ea5b8428c887d144de245876ace755429 */
import React, { useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import menuData from '../config/menuData.js';
import { canAccessSlug } from '../config/roles.js';

/**
 * RBAC filter — keep only the menu entries the current role may reach. Groups
 * are pruned to their visible children and dropped entirely when empty. An
 * anonymous visitor (no role) resolves to the public "user" role, so only
 * public pages (Home) remain.
 */
function visibleMenuFor(role) {
  return menuData
    .filter(Boolean)
    .map((item) => {
      if (item.children) {
        const children = item.children.filter(Boolean).filter((c) => canAccessSlug(role, c.slug));
        if (children.length === 0) return null;
        // Collapse a group that has only one reachable child into a direct
        // top-level link — avoids a dropdown that hides a single buried item.
        if (children.length === 1) return children[0];
        return { ...item, children };
      }
      return canAccessSlug(role, item.slug) ? item : null;
    })
    .filter(Boolean);
}

// Application identity (branding). No logo asset ships in the design, so the
// application name is used as the brand.
const APP_NAME = 'SolarOS';

const routeOf = (page) => (page.slug === 'home' ? '/' : `/${page.slug}`);

function LeafLink({ page, onNavigate }) {
  return (
    <li className="nav-item">
      <NavLink
        to={routeOf(page)}
        className="nav-link text-white"
        onClick={onNavigate}
        end={page.slug === 'home'}
      >
        {page.icon && <i className={`${page.icon} me-2`} aria-hidden="true" />}
        {page.label}
      </NavLink>
    </li>
  );
}

function GroupDropdown({ group, onNavigate }) {
  const id = `menu-${group.label.replace(/\W+/g, '_')}`;
  return (
    <li className="nav-item dropdown">
      <a
        className="nav-link dropdown-toggle text-white"
        href="#"
        id={id}
        role="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        {group.icon && <i className={`${group.icon} me-2`} aria-hidden="true" />}
        {group.label}
      </a>
      <ul className="dropdown-menu" aria-labelledby={id}>
        {group.children.filter(Boolean).map((child) => (
          <li key={child.slug}>
            <NavLink to={routeOf(child)} className="dropdown-item" onClick={onNavigate}>
              {child.icon && <i className={`${child.icon} me-2`} aria-hidden="true" />}
              {child.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function DefaultWebLayout() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  // Role-based navigation: only show what this actor is allowed to reach.
  const menu = useMemo(() => visibleMenuFor(user?.role), [user?.role]);

  const collapse = () => setExpanded(false);

  const handleLogout = () => {
    collapse();
    logout();
    // Return to the public landing (not /login) so no protected page is
    // captured as a stale "intended destination" for the next sign-in.
    navigate('/');
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header / primary navigation */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary fixed-top">
        <div className="container-fluid">
          <Link className="navbar-brand fw-bold" to="/" onClick={collapse}>
            <i className="fa fa-solar-panel me-2" aria-hidden="true" />
            {APP_NAME}
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            aria-controls="navbarNav"
            aria-expanded={expanded}
            aria-label="Toggle navigation"
            onClick={() => setExpanded((v) => !v)}
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className={`collapse navbar-collapse${expanded ? ' show' : ''}`} id="navbarNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              {menu.map((item) =>
                item.children ? (
                  <GroupDropdown key={item.label} group={item} onNavigate={collapse} />
                ) : (
                  <LeafLink key={item.slug} page={item} onNavigate={collapse} />
                )
              )}
            </ul>

            {/* Session / account chrome */}
            <ul className="navbar-nav ms-auto align-items-lg-center">
              {isAuthenticated ? (
                <li className="nav-item dropdown">
                  <a
                    className="nav-link dropdown-toggle text-white d-flex align-items-center"
                    href="#"
                    id="accountMenu"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <img
                      src="https://placehold.co/30"
                      alt=""
                      className="rounded-circle me-2"
                      width={30}
                      height={30}
                    />
                    <span>{user?.name || user?.email || 'Account'}</span>
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="accountMenu">
                    <li>
                      <span className="dropdown-item-text small text-muted">{user?.email}</span>
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <button className="dropdown-item" type="button" onClick={handleLogout}>
                        <i className="fa fa-sign-out-alt me-2" aria-hidden="true" />
                        Logout
                      </button>
                    </li>
                  </ul>
                </li>
              ) : (
                <li className="nav-item">
                  <Link className="btn btn-light btn-sm" to="/login" onClick={collapse}>
                    <i className="fa fa-sign-in-alt me-2" aria-hidden="true" />
                    Login
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>

      {/* Main content — pages inject their body here */}
      <main className="flex-grow-1" style={{ paddingTop: '80px' }}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-dark text-light py-4 mt-auto">
        <div className="container">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
            <span className="mb-0">
              &copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </span>
            <span className="text-muted small">
              Solar registry · compliance · RECs · MRV &amp; billing
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
